import config from '../config/environment';
import supabase from '../config/supabase';
import type { TrainingRecord } from '../types/training';

export interface StudentTrainingBlockSummary {
  id: string;
  label: string;
  position: number;
  muscleGroups: string[];
  durationSeconds: number;
  exerciseCount: number;
}

export interface StudentTrainingListItem {
  id: string;
  studentId: string;
  sourceTrainingId: string | null;
  name: string;
  code: string;
  category: string;
  status: string;
  trainingPeriod: string | null;
  durationMinutes: number;
  assignedAt: string;
  deliveredAt: string | null;
  deliveredByName: string | null;
  blocks: StudentTrainingBlockSummary[];
}

interface StudentTrainingStorage {
  getTrainingById: (id: string) => Promise<TrainingRecord | null>;
  saveTraining: (training: TrainingRecord) => Promise<TrainingRecord>;
}

const FUNCTION_NAME = 'manage-student-training';

class StudentTrainingServiceClass {
  private async getHeaders(): Promise<Record<string, string>> {
    const { data } = await supabase.auth.getSession();

    if (!data.session?.access_token) {
      throw new Error('Usuária não autenticada. Faça login novamente.');
    }

    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${data.session.access_token}`,
      apikey: config.supabaseAnonKey,
    };
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${config.supabaseFunctionsUrl}/${FUNCTION_NAME}${path}`, {
      ...init,
      headers: {
        ...(await this.getHeaders()),
        ...init?.headers,
      },
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(payload?.error ?? 'Não foi possível processar o treino da aluna.');
    }

    return payload as T;
  }

  async list(studentId: string): Promise<StudentTrainingListItem[]> {
    return this.request<StudentTrainingListItem[]>(`?student_id=${encodeURIComponent(studentId)}`);
  }

  async get(studentId: string, studentTrainingId: string): Promise<TrainingRecord | null> {
    try {
      return await this.request<TrainingRecord>(
        `?student_id=${encodeURIComponent(studentId)}&id=${encodeURIComponent(studentTrainingId)}`,
      );
    } catch (error) {
      if (error instanceof Error && error.message === 'Treino da aluna não encontrado.') {
        return null;
      }
      throw error;
    }
  }

  async assign(studentId: string, sourceTrainingId: string): Promise<TrainingRecord> {
    return this.request<TrainingRecord>('', {
      method: 'POST',
      body: JSON.stringify({
        student_id: Number(studentId),
        source_training_id: Number(sourceTrainingId),
      }),
    });
  }

  async save(studentId: string, training: TrainingRecord): Promise<TrainingRecord> {
    return this.request<TrainingRecord>('', {
      method: 'PUT',
      body: JSON.stringify({
        student_id: Number(studentId),
        training,
      }),
    });
  }

  createStorage(studentId: string): StudentTrainingStorage {
    return {
      getTrainingById: (studentTrainingId) => this.get(studentId, studentTrainingId),
      saveTraining: (training) => this.save(studentId, training),
    };
  }
}

export const StudentTrainingService = new StudentTrainingServiceClass();
